import React from 'react';

/**
 * ViewToggle — "show me this same data the other way."
 *
 * SHARED ON PURPOSE. Two customer surfaces want it: the Season calendar (list
 * of recitals + closures, or a month grid) and, next, the Schedule page (dated
 * lessons, or the same dates on a grid). Built once so the two cannot drift
 * into two differently-behaved segmented controls, which is the failure mode
 * the Schedule design pass flagged when it had to resolve this against the
 * existing Scheduled | Past pair.
 *
 * WHY BUTTONS WITH aria-pressed RATHER THAN A TABLIST. On Schedule this sits
 * beside `Scheduled | Past lessons`, which IS a tablist. A second tablist would
 * read to a screen reader as two peer navigations, when this one is a MODIFIER
 * of the first — the dates don't change, only their shape. Pressed-state
 * buttons say that. It also keeps the two controls visually unequal, which is
 * the point: full-width segmented pair, compact icon pair.
 *
 * READ-ONLY IS THE CALLER'S JOB, NOT THIS COMPONENT'S — but it is worth saying
 * here because this control is what introduces a calendar to a page: a day cell
 * must never become a second write path. It may at most jump to the row that
 * already owns the action.
 *
 * The labels are visually hidden rather than absent: an icon-only control with
 * no accessible name is unusable with a screen reader, and "list"/"month" are
 * two words.
 */
const ViewToggle = ({ value, onChange, label = 'View', options }) => {
	const views = options || [
		{ key: 'list', icon: 'fa-list', label: 'List' },
		{ key: 'calendar', icon: 'fa-calendar-days', label: 'Month' },
	];

	return (
		<div className='cust-vtog' role='group' aria-label={label}>
			{views.map((v) => {
				const active = value === v.key;
				return (
					<button
						key={v.key}
						type='button'
						className={`cust-vtog__btn${active ? ' is-on' : ''}`}
						aria-pressed={active}
						onClick={() => !active && onChange(v.key)}>
						<i className={`fa-sharp fa-solid ${v.icon}`} aria-hidden='true'></i>
						<span className='cust-vtog__label'>{v.label}</span>
					</button>
				);
			})}
		</div>
	);
};

export default ViewToggle;
